/**
 * Tag list state machine - replaces useTagStore (Zustand)
 */
import { setup, assign } from 'xstate'
import type { CrmTag } from '../types'
import { getAllDescendants } from '../tag-tree'

export interface TagListContext {
  tags: CrmTag[]
  isLoading: boolean
}

export type TagListEvent =
  | { type: 'SET_TAGS'; tags: CrmTag[] }
  | { type: 'ADD_TAG'; tag: CrmTag }
  | { type: 'UPDATE_TAG'; id: string; updates: Partial<CrmTag> }
  | { type: 'REMOVE_TAG'; id: string }
  | { type: 'REMOVE_CASCADE'; id: string }
  | { type: 'SET_LOADING'; loading: boolean }
  | { type: 'CLEAR' }

const machineSetup = setup({
  types: {
    context: {} as TagListContext,
    events: {} as TagListEvent,
  },
})

export const tagListMachine = machineSetup.createMachine({
  id: 'tagList',
  context: {
    tags: [],
    isLoading: false,
  },
  on: {
    SET_TAGS: {
      actions: assign({
        tags: ({ event }) => (event.type === 'SET_TAGS' ? event.tags : []),
      }),
    },
    ADD_TAG: {
      actions: assign({
        tags: ({ context, event }) =>
          event.type === 'ADD_TAG' ? [...context.tags, event.tag] : context.tags,
      }),
    },
    UPDATE_TAG: {
      actions: assign({
        tags: ({ context, event }) => {
          if (event.type !== 'UPDATE_TAG') return context.tags
          return context.tags.map((t) => (t.id === event.id ? { ...t, ...event.updates } : t))
        },
      }),
    },
    REMOVE_TAG: {
      actions: assign({
        tags: ({ context, event }) => {
          if (event.type !== 'REMOVE_TAG') return context.tags
          return context.tags.filter((t) => t.id !== event.id)
        },
      }),
    },
    REMOVE_CASCADE: {
      actions: assign(({ context, event }) => {
        if (event.type !== 'REMOVE_CASCADE') return {}
        const descendants = getAllDescendants(context.tags, event.id)
        const idsToRemove = new Set([event.id, ...descendants.map((d) => d.id)])
        return { tags: context.tags.filter((t) => !idsToRemove.has(t.id)) }
      }),
    },
    SET_LOADING: {
      actions: assign({
        isLoading: ({ event }) => (event.type === 'SET_LOADING' ? event.loading : false),
      }),
    },
    CLEAR: {
      actions: assign({ tags: [], isLoading: false }),
    },
  },
})
